import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { SearchableSelect } from '@/components/common/searchable-select';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import {
  collectVersions,
  type OnFarmImplantQueueFieldChanges,
  type OnFarmImplantQueueRecord,
  OnFarmImplantRequestError,
  useUpdateOnFarmImplantQueueFields,
} from '@/services/on-farm-implant';
import { parseWallClock, toWallClockDate } from '@/utils';

import { IMPLANT_QUEUE_FIELD_LABEL_KEYS } from './columnDefs';
import { useImplantQueueFieldOptions } from './fieldOptions';

export type MassChangeField =
  | 'implantDate'
  | 'quality'
  | 'distance'
  | 'implantTechId'
  | 'implantType'
  | 'farmLocationId';

const MASS_CHANGE_FIELDS: MassChangeField[] = [
  'implantDate',
  'quality',
  'distance',
  'implantTechId',
  'implantType',
  'farmLocationId',
];

type MassChangeValues = Record<MassChangeField, string>;

const EMPTY_VALUES: MassChangeValues = {
  implantDate: '',
  quality: '',
  distance: '',
  implantTechId: '',
  implantType: '',
  farmLocationId: '',
};

const EMPTY_APPLY: Record<MassChangeField, boolean> = {
  implantDate: false,
  quality: false,
  distance: false,
  implantTechId: false,
  implantType: false,
  farmLocationId: false,
};

const NUMERIC_FIELDS: ReadonlySet<MassChangeField> = new Set([
  'quality',
  'distance',
  'implantType',
]);

type MassChangeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  rows: OnFarmImplantQueueRecord[];
  onApplied: (
    rows: OnFarmImplantQueueRecord[],
    changes: OnFarmImplantQueueFieldChanges,
  ) => void;
};

export const MassChangeDialog = ({
  open,
  onOpenChange,
  sessionId,
  rows,
  onApplied,
}: MassChangeDialogProps) => {
  const { t } = useTranslation();
  const { techOptions, farmLocationOptions, implantTypeOptions } =
    useImplantQueueFieldOptions();

  const [values, setValues] = useState<MassChangeValues>(EMPTY_VALUES);
  const [apply, setApply] = useState(EMPTY_APPLY);
  const [problems, setProblems] = useState<string[]>([]);

  const { mutateAsync: updateFields, isPending } =
    useUpdateOnFarmImplantQueueFields();

  const implantTypeItems = useMemo(
    () =>
      implantTypeOptions.map((option) => ({
        value: String(option.value),
        label: option.label,
      })),
    [implantTypeOptions],
  );

  const appliedFields = MASS_CHANGE_FIELDS.filter((field) => apply[field]);

  const invalidNumber = appliedFields.some(
    (field) =>
      (field === 'quality' || field === 'distance') &&
      values[field].trim() !== '' &&
      !/^\d+$/.test(values[field].trim()),
  );

  const setValue = useCallback((field: MassChangeField, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    setApply((current) => ({ ...current, [field]: true }));
  }, []);

  const setApplyField = useCallback(
    (field: MassChangeField, checked: boolean) => {
      setApply((current) => ({ ...current, [field]: checked }));
    },
    [],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setValues(EMPTY_VALUES);
        setApply(EMPTY_APPLY);
        setProblems([]);
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const handleConfirm = useCallback(async () => {
    if (rows.length === 0 || appliedFields.length === 0 || invalidNumber) {
      return;
    }

    const changes: OnFarmImplantQueueFieldChanges = {};
    for (const field of appliedFields) {
      const raw = values[field].trim();
      changes[field] = {
        value:
          raw === '' ? null : NUMERIC_FIELDS.has(field) ? Number(raw) : raw,
      };
    }

    setProblems([]);

    try {
      const result = await updateFields({
        sessionId,
        input: {
          sessionQueueIds: rows.map((row) => row.sessionQueueId),
          changes,
          versions: collectVersions(rows),
        },
      });

      onApplied(rows, changes);
      handleOpenChange(false);

      toast.add({
        data: { variant: 'success' },
        title: t('onFarmImplant.implantQueue.massChange.success', {
          count: result.items.length,
        }),
      });
    } catch (error) {
      if (error instanceof OnFarmImplantRequestError && error.problems.length) {
        setProblems(error.problems);
        return;
      }

      console.error('Failed to mass change queue rows:', error);
      toast.add({
        data: { variant: 'error' },
        title: t('onFarmImplant.implantQueue.massChange.failed'),
      });
    }
  }, [
    appliedFields,
    handleOpenChange,
    invalidNumber,
    onApplied,
    rows,
    sessionId,
    t,
    updateFields,
    values,
  ]);

  const renderInput = (field: MassChangeField) => {
    const inputId = `mass-change-${field}`;
    switch (field) {
      case 'implantDate':
        return (
          <DatePicker
            id={inputId}
            value={parseWallClock(values.implantDate) ?? undefined}
            onChange={(date) =>
              setValue('implantDate', date ? toWallClockDate(date) : '')
            }
            placeholder={t('onFarmImplant.implantQueue.massChange.selectDate')}
            className="h-8 w-full text-xs"
          />
        );
      case 'quality':
      case 'distance':
        return (
          <Input
            id={inputId}
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            className="h-8 text-xs"
            value={values[field]}
            onChange={(event) => setValue(field, event.target.value)}
          />
        );
      case 'implantTechId':
        return (
          <SearchableSelect
            className="h-8 w-full text-xs"
            showClear
            options={techOptions}
            value={values.implantTechId}
            onChange={(value) => setValue('implantTechId', value)}
            placeholder={t('onFarmImplant.implantQueue.massChange.selectTech')}
            emptyMessage={t('onFarmImplant.implantQueue.massChange.noOptions')}
          />
        );
      case 'farmLocationId':
        return (
          <SearchableSelect
            className="h-8 w-full text-xs"
            showClear
            options={farmLocationOptions}
            value={values.farmLocationId}
            onChange={(value) => setValue('farmLocationId', value)}
            placeholder={t(
              'onFarmImplant.implantQueue.massChange.selectFarmLocation',
            )}
            emptyMessage={t('onFarmImplant.implantQueue.massChange.noOptions')}
          />
        );
      case 'implantType':
        return (
          <Select
            items={implantTypeItems}
            value={values.implantType}
            onChange={(value) =>
              setValue('implantType', (value as string | null) ?? '')
            }
          >
            <SelectTrigger id={inputId} className="h-8 w-full text-xs">
              <SelectValue
                placeholder={t(
                  'onFarmImplant.implantQueue.massChange.selectImplantType',
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {implantTypeItems.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="md:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t('onFarmImplant.implantQueue.massChange.title', {
              count: rows.length,
            })}
          </DialogTitle>
          <DialogDescription>
            {t('onFarmImplant.implantQueue.massChange.description')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          {problems.length > 0 && (
            <div
              role="alert"
              className="rounded-md bg-red-50 p-3 text-xs text-red-700"
            >
              <ul className="list-inside list-disc">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}

          {MASS_CHANGE_FIELDS.map((field) => {
            const label = t(IMPLANT_QUEUE_FIELD_LABEL_KEYS[field]);
            return (
              <div
                key={field}
                className="grid grid-cols-[auto_8rem_1fr] items-center gap-3"
              >
                <Checkbox
                  checked={apply[field]}
                  onCheckedChange={(checked) => setApplyField(field, checked)}
                  aria-label={t('onFarmImplant.implantQueue.massChange.apply', {
                    field: label,
                  })}
                />
                <Label className="text-xs" htmlFor={`mass-change-${field}`}>
                  {label}
                </Label>
                {renderInput(field)}
              </div>
            );
          })}

          {invalidNumber && (
            <p className="text-xs text-red-600">
              {t('onFarmImplant.implantQueue.massChange.invalidNumber')}
            </p>
          )}

          <p className="text-sm font-medium text-gray-700">
            {t('onFarmImplant.implantQueue.massChange.recordCount', {
              count: rows.length,
            })}
          </p>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              rows.length === 0 ||
              appliedFields.length === 0 ||
              invalidNumber ||
              isPending
            }
          >
            {isPending && <i className="pi pi-spin pi-spinner" />}
            {t('onFarmImplant.implantQueue.massChange.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
